import { useState, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Plus, Loader2, Download, Megaphone } from 'lucide-react';
import { Link } from '@tanstack/react-router';
import { EmptyState, ErrorState, useToast } from '@mustauto/ui/web';
import { motion } from 'framer-motion';
import { api } from '@/lib/api';
import { Badge } from '@/components/ui/badge';
import { Card, CardHeader, CardTitle, CardContent } from '@mustauto/ui/web';
import { SkeletonTable } from '@mustauto/ui/web';
import {
  Table,
  TableHeader,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
} from '@mustauto/ui/web';
import { formatNumber, formatPercent } from '@/lib/utils';

function getCurrentMonth(): string {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
}

interface DashboardCampaign {
  id: string;
  name: string;
  category: string;
  regions: string[] | null;
  active: boolean;
  impressions: number;
  clicks: number;
  ctr: number;
  budgetUsed?: number;
  budgetTotal?: number;
  status?: 'active' | 'paused' | 'ended';
}

function BudgetBar({ used, total }: { used: number; total: number }) {
  const pct = total > 0 ? Math.min((used / total) * 100, 100) : 0;
  const color =
    pct > 85
      ? 'bg-red-500 dark:bg-red-400'
      : pct > 60
        ? 'bg-amber-500 dark:bg-amber-400'
        : 'bg-emerald-500 dark:bg-emerald-400';

  return (
    <div className="flex items-center gap-2">
      <div className="h-2 w-16 rounded-full bg-muted">
        <div
          className={`h-2 rounded-full ${color} transition-all`}
          style={{ width: `${pct}%` }}
        />
      </div>
      <span className="text-xs text-muted-foreground">{Math.round(pct)}%</span>
    </div>
  );
}

function resolveStatus(c: DashboardCampaign): 'active' | 'paused' | 'ended' {
  if (c.status) return c.status;
  return c.active ? 'active' : 'paused';
}

function StatusPill({ status }: { status: 'active' | 'paused' | 'ended' }) {
  const map = {
    active: { label: 'Ativa', variant: 'success' as const },
    paused: { label: 'Pausada', variant: 'secondary' as const },
    ended: { label: 'Encerrada', variant: 'destructive' as const },
  };
  const { label, variant } = map[status];
  return <Badge variant={variant}>{label}</Badge>;
}

const rowVariants = {
  hidden: { opacity: 0, y: 8 },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.03, duration: 0.25, ease: 'easeOut' as const },
  }),
};

export function CampaignsPage() {
  const [exportingAll, setExportingAll] = useState(false);
  const { toast } = useToast();

  const { data, isLoading, isError, error, refetch } = useQuery<{ activeCampaigns: DashboardCampaign[] }>({
    queryKey: ['partner-campaigns'],
    queryFn: async () => {
      const res = await api.get('/partners/my/dashboard', {
        params: { month: getCurrentMonth() },
      });
      return res.data;
    },
  });

  const campaigns = data?.activeCampaigns ?? [];

  const handleExportAll = useCallback(async () => {
    setExportingAll(true);
    try {
      const res = await api.get('/partners/my/campaigns/export-all', {
        params: { format: 'pdf', month: getCurrentMonth() },
        responseType: 'blob',
      });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `mustauto-campanhas-todas-${getCurrentMonth()}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast({
        type: 'error',
        title: 'Não foi possível gerar o PDF. Tente novamente.',
      });
    } finally {
      setExportingAll(false);
    }
  }, [toast]);

  return (
    <motion.div
      className="space-y-6"
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, ease: 'easeOut' }}
    >
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Campanhas</h1>
          <p className="text-sm text-muted-foreground">
            Gerencie suas campanhas ativas e crie novas
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={handleExportAll}
            disabled={exportingAll || campaigns.length === 0}
            className="inline-flex items-center gap-2 rounded-lg border border-border bg-card px-4 py-2.5 text-sm font-medium text-foreground transition-colors hover:bg-muted disabled:opacity-50"
          >
            {exportingAll ? (
              <Loader2 size={16} className="animate-spin" />
            ) : (
              <Download size={16} />
            )}
            Exportar todas
          </button>
          <Link
            to="/campaigns/new"
            className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2.5 text-sm font-semibold text-primary-foreground transition-opacity hover:opacity-90"
          >
            <Plus size={16} />
            Nova campanha
          </Link>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Todas as campanhas</CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <SkeletonTable rows={5} />
          ) : isError && campaigns.length === 0 ? (
            /* O vazio precisa vir DEPOIS do erro. O hook devolve
               `data?.activeCampaigns ?? []`, entao a busca que falha chegava
               aqui como lista vazia e a tela dizia a quem tem doze campanhas no
               ar que nao tem nenhuma — e o convidava a criar a primeira.
               O `length === 0` importa: com a lista ja em cache, uma
               revalidacao que falha nao apaga dado bom. */
            <ErrorState
              title="Não foi possível carregar suas campanhas"
              error={error}
              onRetry={() => void refetch()}
            />
          ) : campaigns.length === 0 ? (
            <EmptyState
              icon={<Megaphone size={28} />}
              title="Nenhuma campanha cadastrada"
              description="Crie sua primeira campanha para começar a promover seus produtos nas oficinas parceiras."
              action={{ label: 'Criar primeira campanha', href: '/campaigns/new' }}
            />
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Campanha</TableHead>
                  <TableHead>Categoria</TableHead>
                  <TableHead>Regiões</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Impressões</TableHead>
                  <TableHead className="text-right">Cliques</TableHead>
                  <TableHead className="text-right">CTR</TableHead>
                  <TableHead>Orçamento</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {campaigns.map((c, i) => {
                  const status = resolveStatus(c);
                  return (
                    <motion.tr
                      key={c.id}
                      custom={i}
                      variants={rowVariants}
                      initial="hidden"
                      animate="visible"
                      className="border-b border-border transition-colors hover:bg-muted/50"
                    >
                      <TableCell className="font-medium">
                        <Link
                          to="/campaigns/$campaignId"
                          params={{ campaignId: c.id }}
                          className="text-primary hover:underline"
                        >
                          {c.name}
                        </Link>
                      </TableCell>
                      <TableCell>{c.category}</TableCell>
                      <TableCell>
                        {c.regions && c.regions.length > 0
                          ? c.regions.join(', ')
                          : 'Todas'}
                      </TableCell>
                      <TableCell>
                        <StatusPill status={status} />
                      </TableCell>
                      <TableCell className="text-right">
                        {formatNumber(c.impressions)}
                      </TableCell>
                      <TableCell className="text-right">{formatNumber(c.clicks)}</TableCell>
                      <TableCell className="text-right">{formatPercent(c.ctr)}</TableCell>
                      <TableCell>
                        <BudgetBar
                          used={c.budgetUsed ?? 0}
                          total={c.budgetTotal ?? 0}
                        />
                      </TableCell>
                    </motion.tr>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </motion.div>
  );
}
